import { forwardRef, useMemo, useState } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';
import { useNavigate, useParams } from 'react-router-dom';

import { formatContent, REF_PLACEHOLDER_TAG } from './kim-message';
import { Reference } from './reference';
import { KIM_MARKED_OPTIONS } from '../../utils/kim-markdown';
import {
  ContentBlock,
  DocumentInconsistenciesBlock,
  InconsistencyBlock,
  ReferenceType,
} from '@/../../backend/src/types/types';
import { useGetKimMessages } from '@/api/queries/useGetKimMessages';
import { AnchorDocViewer, AnchorReference, useAnchorDocFindings } from '@/new/components/anchor-doc';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { cn } from '@/new/lib/utils';

export function VerificationViewer() {
  const { matterId, subsetId, threadId, blockId } = useParams<{
    matterId: string;
    subsetId: string;
    threadId: string;
    blockId: string;
  }>();
  const navigate = useNavigate();

  const { data: messages, isLoading: isLoadingMessages } = useGetKimMessages(matterId, subsetId, threadId);

  const block = useMemo<DocumentInconsistenciesBlock | null>(() => {
    if (!messages || !blockId) return null;
    for (const msg of messages) {
      for (const content of msg.content) {
        if (content.type === 'document_inconsistencies' && content.id === blockId) {
          return content;
        }
      }
    }
    return null;
  }, [messages, blockId]);

  const items = block?.content ?? [];

  // Project the domain items down to the shape the hook needs. Collect every
  // highlightable reference (facts, chunks, thread-file chunks) from both the
  // evidence references and the inline description chips so anchor-doc detection
  // works for description-only citations too.
  const findingsItems = useMemo(
    () =>
      items.map((item) => {
        const references = new Map<string, { id: string; refType: ReferenceType }>();
        for (const ref of item.references ?? []) {
          if (ANCHOR_HIGHLIGHTABLE_REF_TYPES.has(ref.reference.refType)) {
            references.set(ref.reference.id, { id: ref.reference.id, refType: ref.reference.refType });
          }
        }
        for (const block of item.description ?? []) {
          if (block.type === 'reference' && ANCHOR_HIGHLIGHTABLE_REF_TYPES.has(block.refType)) {
            references.set(block.id, { id: block.id, refType: block.refType });
          }
        }
        return { id: item.id, references: Array.from(references.values()) };
      }),
    [items],
  );

  const findings = useAnchorDocFindings({
    matterId,
    subsetId,
    threadId,
    anchorDocId: block?.anchorDocId,
    anchorDocType: block?.anchorDocType,
    items: findingsItems,
  });

  return (
    <AnchorDocViewer
      findings={findings}
      items={items}
      header={
        <div className="flex items-center gap-3 border-b bg-white px-4 py-3">
          <Button
            size="sm"
            variant="ghost"
            className="px-0"
            onClick={() => navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/${threadId}`)}
          >
            <Icon className="lucide--arrow-left" />
            <span>Back to thread</span>
          </Button>
          <div className="h-5 w-px bg-gray-200" />
          <div className="min-w-0 flex-1">
            {isLoadingMessages ? (
              <Skeleton className="h-4 w-48" />
            ) : (
              <p className="truncate font-sans text-sm font-medium text-gray-800">{findings.anchorDocName}</p>
            )}
          </div>
          <p className="mr-1 font-sans text-[11px] text-gray-500">
            {items.length} finding{items.length === 1 ? '' : 's'}
          </p>
        </div>
      }
      renderItem={(item, ctx) => (
        <VerificationItemCard
          key={item.id}
          ref={ctx.registerRef}
          item={item}
          anchorReferences={findings.anchorReferencesByItemId.get(item.id) ?? []}
          isActive={ctx.isActive}
          onActivate={ctx.onActivate}
          onReferenceClick={findings.handleDescriptionReferenceClick}
        />
      )}
    />
  );
}

interface VerificationItemCardProps {
  item: InconsistencyBlock;
  anchorReferences: AnchorReference[];
  isActive: boolean;
  onActivate: () => void;
  onReferenceClick: (id: string, referenceType: ReferenceType) => void;
}

function VerificationItemCardImpl(
  { item, anchorReferences, isActive, onActivate, onReferenceClick }: VerificationItemCardProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const referenceIndexMap = useMemo(
    () => buildDescriptionReferenceIndexMap(item.description ?? []),
    [item.description],
  );

  const description = useMemo(
    () => renderDescription(item.description ?? [], referenceIndexMap, onReferenceClick),
    [item.description, referenceIndexMap, onReferenceClick],
  );

  const anchorPagesLabel = useMemo(() => formatAnchorPagesLabel(anchorReferences), [anchorReferences]);

  const [expanded, setExpanded] = useState(false);

  return (
    <li
      ref={ref}
      onClick={onActivate}
      className={cn(
        'cursor-pointer rounded-lg border border-l-1 bg-white px-3.5 py-3 duration-150',
        isActive ? 'border-gray-200 border-l-red-400 shadow-lg/1' : 'border-gray-200 hover:border-gray-300',
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        {anchorPagesLabel !== null && <span className="font-sans text-[11px] text-gray-400">{anchorPagesLabel}</span>}

        {/* TODO: Add investigation button here which will open the item in a new kim thread */}
      </div>

      <div>
        <div className={cn('max-w-none text-[12px]', !expanded && 'line-clamp-4')}>{description}</div>

        <Button
          type="button"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-1.5 p-0 font-sans text-[11px] text-gray-500 duration-150 hover:text-gray-700"
        >
          {expanded ? 'Show less' : 'Show more'}
          <Icon className="lucide--chevron-down" />
        </Button>
      </div>
    </li>
  );
}

const VerificationItemCard = forwardRef<HTMLLIElement, VerificationItemCardProps>(VerificationItemCardImpl);

function formatAnchorPagesLabel(anchorReferences: AnchorReference[]): string | null {
  if (anchorReferences.length === 0) return null;

  const pages = new Set<number>();
  for (const ref of anchorReferences) {
    for (const box of ref.boundingBoxes) {
      pages.add(box.pageNumber);
    }
  }

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  if (sortedPages.length === 0) return null;
  if (sortedPages.length === 1) return `p. ${sortedPages[0]}`;
  return `pp. ${sortedPages.join(', ')}`;
}

function buildDescriptionReferenceIndexMap(
  description: ContentBlock[],
): Map<string, { index: number; referenceType: ReferenceType }> {
  const map = new Map<string, { index: number; referenceType: ReferenceType }>();
  let index = 1;
  for (const block of description) {
    if (block.type === 'reference' && !map.has(block.id)) {
      map.set(block.id, { index: index++, referenceType: block.refType });
    }
  }
  return map;
}

// Reference types that can resolve to a bounding box on the anchor doc and so
// be rendered as a highlight / drive the per-item page label.
const ANCHOR_HIGHLIGHTABLE_REF_TYPES = new Set<ReferenceType>([
  ReferenceType.Fact,
  ReferenceType.Chunk,
  ReferenceType.ThreadFileChunk,
]);

const CITABLE_REF_TYPES = new Set<ReferenceType>([
  ReferenceType.Fact,
  ReferenceType.Chunk,
  ReferenceType.Document,
  ReferenceType.ThreadFileChunk,
]);

function renderDescription(
  blocks: ContentBlock[],
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>,
  onReferenceClick: (id: string, referenceType: ReferenceType) => void,
) {
  const combined = formatContent(blocks);
  const rawHtml = marked.parse(combined, KIM_MARKED_OPTIONS) as string;
  const html = rawHtml.replace(new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'), '$1$2');
  return parse(html, {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
        const id = domNode.attribs['data-id'];
        const refType = domNode.attribs['data-ref-type'] as ReferenceType;
        return (
          <Reference
            index={referenceIndexMap.get(id)?.index ?? 0}
            id={id}
            referenceType={refType}
            onClick={CITABLE_REF_TYPES.has(refType) ? onReferenceClick : undefined}
          />
        );
      }
    },
  });
}
